<!-- ClassPage.vue - CLEAN MODERN DESIGN -->
<template>
  <main class="flex-grow container mx-auto p-4 sm:p-6">
    <!-- Loading Spinner -->
    <div v-if="isLoading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
    </div>

    <!-- Error Message -->
    <div v-else-if="errorMessage" class="text-center py-16">
      <svg class="w-16 h-16 mx-auto mb-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-red-600 dark:text-red-400">{{ errorMessage }}</p>
    </div>

    <!-- Class Content -->
    <div v-else>
      <!-- Class Heading -->
      <h2 class="text-3xl sm:text-4xl font-bold mb-8 text-center text-slate-900 dark:text-white">
        {{ classData.section_display_name }}
      </h2>

      <!-- Class Overview and Assignments Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <!-- Class Overview -->
        <div class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50">
          <h3 class="text-2xl font-bold mb-6 text-slate-900 dark:text-white">Class Overview</h3>
          <ul class="space-y-4 text-slate-700 dark:text-slate-300">
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <span class="font-semibold">Cumulative Grade: </span>
                <span>{{ classData.cumulative_grade !== null ? classData.cumulative_grade + '%' : 'N/A' }}</span>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <span class="font-semibold">Next Meeting: </span>
                <span>{{ nextMeetingTime ? formatDateTime(nextMeetingTime) : 'No upcoming meetings' }}</span>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <div>
                <span class="font-semibold">Next Assignment Due: </span>
                <span v-if="nextAssignmentDue">
                  {{ nextAssignmentDue.display_name }} - {{ formatDate(nextAssignmentDue.display_due_date) }}
                </span>
                <span v-else>No upcoming assignments</span>
              </div>
            </li>
          </ul>
        </div>

        <!-- Upcoming Assignments -->
        <div class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50">
          <h3 class="text-2xl font-bold mb-6 text-slate-900 dark:text-white">Upcoming Assignments</h3>
          <ul v-if="upcomingAssignments.length" class="space-y-3">
            <li
                v-for="assignment in upcomingAssignments"
                :key="assignment.assignment_id"
                class="bg-slate-50 dark:bg-slate-900 hover:bg-white dark:hover:bg-slate-800 rounded-lg p-4 transition-all duration-200 cursor-pointer border border-slate-200 dark:border-slate-700"
                @click="openAssignmentModal(assignment)"
            >
              <div class="flex justify-between items-start gap-3">
                <div class="flex items-start gap-2 flex-1">
                  <div class="w-1 h-8 bg-indigo-600 rounded-full flex-shrink-0"></div>
                  <span class="font-semibold text-slate-900 dark:text-white">{{ assignment.display_name }}</span>
                </div>
                <span class="text-slate-500 dark:text-slate-400 text-sm whitespace-nowrap">
                  {{ formatDate(assignment.display_due_date) }}
                </span>
              </div>
            </li>
          </ul>
          <div v-else class="text-center py-8">
            <svg class="w-12 h-12 mx-auto mb-2 text-slate-300 dark:text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="text-slate-500 dark:text-slate-400">No upcoming assignments</p>
          </div>
        </div>
      </div>

      <Gradebook :key="section_id" :assignments="gradedAssignments" :official-grade="classData.cumulative_grade" />
    </div>

    <!-- Modal for Assignment Description -->
    <div
        v-if="showAssignmentModal"
        class="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm p-4"
    >
      <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 max-w-2xl w-full relative border border-slate-200 dark:border-slate-700">
        <button
            @click="closeAssignmentModal"
            class="absolute top-6 right-6 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h3 class="text-2xl font-bold mb-4 text-slate-900 dark:text-white pr-8">
          {{ currentAssignment.display_name }}
        </h3>
        <p class="text-slate-700 dark:text-slate-300 mb-6 whitespace-pre-wrap leading-relaxed">
          {{ currentAssignment.display_description }}
        </p>

        <div class="flex justify-end">
          <button
              @click="closeAssignmentModal"
              class="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>

  </main>
</template>

<script>
import axios from 'axios';
import Gradebook from '@/components/grades/Gradebook.vue';
import he from 'he';
import sanitizeHtml from 'sanitize-html';
import { getActiveAuthToken, redirectToLogin as beginLogin } from '@/config/auth';
import { backendUrl } from '@/config/backend';

const DEBUG_CLASS_PAGE = true;
const debugLog = (...args) => {
  if (DEBUG_CLASS_PAGE) console.log(...args);
};
const debugWarn = (...args) => {
  if (DEBUG_CLASS_PAGE) console.warn(...args);
};

export default {
  components: { Gradebook },
  props: {
    section_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      token: null,
      isLoading: true,
      errorMessage: '',
      classData: null,
      nextMeetingTime: null,
      nextAssignmentDue: null,
      upcomingAssignments: [],
      gradedAssignments: [],
      showAssignmentModal: false,
      currentAssignment: null
    };
  },
  methods: {
    extractToken() {
      const storedToken = getActiveAuthToken();
      if (storedToken) {
        this.token = storedToken;
      } else {
        this.redirectToLogin();
      }
    },
    isTokenExpired(token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp;
        const now = Math.floor(new Date().getTime() / 1000);
        return now > expiry;
      } catch (error) {
        console.error('Invalid token format:', error);
        return true;
      }
    },
    redirectToLogin() {
      beginLogin({ originalUrl: window.location.href });
    },
    formatDate(dateString) {
      const date = this.parseDateTime(dateString);
      if (!date) return 'Invalid Date';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    formatDateTime(dateTimeString) {
      const date = this.parseDateTime(dateTimeString);
      if (!date) return 'Invalid Date';
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
    formatApiDate(date) {
      const easternFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      return easternFormatter.format(date);
    },
    parseDateTime(dateTimeString) {
      if (!dateTimeString) return null;
      const normalized = String(dateTimeString).trim();
      if (!normalized) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
        const [year, month, day] = normalized.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      const isoString = normalized.replace(' ', 'T');
      const date = new Date(isoString);
      if (Number.isNaN(date.getTime())) {
        debugWarn('[ClassPage] Invalid date string:', dateTimeString);
        return null;
      }
      return date;
    },
    normalizeText(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    },
    normalizeAssignment(assignment) {
      if (!assignment) return null;
      const displayName = this.cleanField(
        assignment.display_name || assignment.short_description || assignment.name || assignment.title || ''
      );
      const displayDescription = this.cleanField(
        assignment.display_description || assignment.long_description || assignment.description || ''
      );
      const displayDueDate =
        assignment.display_due_date ||
        assignment.date_due ||
        assignment.due_date ||
        assignment.dueDate ||
        assignment.due_at ||
        null;
      if (!displayDueDate) {
        debugWarn('[ClassPage] Assignment missing due date fields:', {
          assignment_id: assignment.assignment_id,
          name: assignment.name,
          short_description: assignment.short_description,
          date_due: assignment.date_due,
          due_date: assignment.due_date,
          dueDate: assignment.dueDate,
          due_at: assignment.due_at
        });
      }
      if (!displayName) {
        debugWarn('[ClassPage] Assignment missing name fields:', {
          assignment_id: assignment.assignment_id,
          name: assignment.name,
          short_description: assignment.short_description,
          title: assignment.title,
          display_name: assignment.display_name
        });
      }
      return {
        ...assignment,
        display_name: displayName,
        display_description: displayDescription,
        display_due_date: displayDueDate
      };
    },
    getClassMatchTokens() {
      if (!this.classData) return [];
      const tokens = [
        this.classData.section_display_name,
        this.classData.section_name,
        this.classData.course_title,
        this.classData.course_name,
        this.classData.name
      ]
        .filter(Boolean)
        .map((value) => this.normalizeText(this.cleanField(value)));
      return Array.from(new Set(tokens)).filter(Boolean);
    },
    matchesClass(assignment, classTokens) {
      if (!assignment) return false;
      if (assignment.section_id && this.section_id && String(assignment.section_id) === String(this.section_id)) {
        return true;
      }
      const assignmentSection = this.normalizeText(
        this.cleanField(
          assignment.section_name ||
          assignment.section_display_name ||
          assignment.course_title ||
          assignment.course_name ||
          ''
        )
      );
      if (!assignmentSection) return false;
      return classTokens.some((token) =>
        token === assignmentSection ||
        token.includes(assignmentSection) ||
        assignmentSection.includes(token)
      );
    },
    async fetchAssignmentsForClass() {
      if (!this.token || this.isTokenExpired(this.token)) return null;
      try {
        const start_date = this.formatApiDate(new Date());
        const end_date = this.formatApiDate(new Date(Date.now() + 90 * 24 * 60 * 60 * 1000));
        const response = await axios.get(backendUrl('/assignments'), {
          params: { start_date, end_date },
          headers: { Authorization: `Bearer ${this.token}` }
        });
        const assignments = Array.isArray(response.data?.value) ? response.data.value : [];
        const classTokens = this.getClassMatchTokens();
        debugLog('[ClassPage] fallback assignments fetched:', assignments.length);
        debugLog('[ClassPage] class match tokens:', classTokens);

        if (!classTokens.length && !(assignments || []).some(a => a.section_id)) {
          debugWarn('[ClassPage] No class tokens and no section_id fields on assignments; matching may fail.');
        }

        const matchingAssignments = assignments.filter((assignment) => this.matchesClass(assignment, classTokens));
        debugLog('[ClassPage] fallback assignments matched:', matchingAssignments.length);

        const normalizedAssignments = matchingAssignments
          .map((assignment) => this.normalizeAssignment(assignment))
          .filter(Boolean);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const upcomingAssignments = normalizedAssignments
          .filter((assignment) => {
            const dueDate = this.parseDateTime(assignment.display_due_date);
            return dueDate && dueDate >= today;
          })
          .sort((a, b) => this.parseDateTime(a.display_due_date) - this.parseDateTime(b.display_due_date));

        const nextAssignmentDue = upcomingAssignments.length ? upcomingAssignments[0] : null;

        debugLog('[ClassPage] fallback upcoming assignments:', upcomingAssignments.length);
        debugLog('[ClassPage] fallback nextAssignmentDue:', nextAssignmentDue);

        return { upcomingAssignments, nextAssignmentDue };
      } catch (error) {
        console.error('Error fetching fallback assignments:', error);
        return null;
      }
    },
    openAssignmentModal(assignment) {
      this.currentAssignment = this.normalizeAssignment(assignment);
      this.showAssignmentModal = true;
    },
    closeAssignmentModal() {
      this.showAssignmentModal = false;
      this.currentAssignment = null;
    },
    cleanField(field) {
      if (field == null) return '';
      let withLineBreaks = String(field).replace(/<br\s*\/?>/gi, '\n').replace(/<\/?p>/gi, '\n');
      for (let i = 0; i < 5; i += 1) {
        const next = he.decode(withLineBreaks);
        if (next === withLineBreaks) break;
        withLineBreaks = next;
      }
      const cleanText = sanitizeHtml(withLineBreaks, { allowedTags: [], allowedAttributes: {} });
      return he.decode(cleanText).trim();
    },

    async fetchClassData() {
      const startTime = performance.now();
      try {
        const response = await axios.get(
          backendUrl(`/classes/${this.section_id}/page`),
          {
            headers: { Authorization: `Bearer ${this.token}` }
          }
        );
        const endTime = performance.now();
        console.log(`[ClassPage] fetchClassData took ${(endTime - startTime).toFixed(2)}ms`);

        const { classData, classDataError } = response.data;
        debugLog('[ClassPage] response keys:', Object.keys(response.data || {}));
        debugLog('[ClassPage] classData keys:', Object.keys(classData || {}));
        debugLog('[ClassPage] classData assignment-like keys:', Object.keys(classData || {}).filter(key => key.toLowerCase().includes('assign')));
        debugLog('[ClassPage] gradeSettings keys:', Object.keys(response.data?.gradeSettings || {}));
        debugLog('[ClassPage] userGrades keys:', Object.keys(response.data?.userGrades || {}));
        debugLog('[ClassPage] raw next_assignment_due:', classData?.next_assignment_due);
        debugLog('[ClassPage] assignments count:', Array.isArray(classData?.assignments) ? classData.assignments.length : 0);
        debugLog('[ClassPage] sample assignment fields:', (classData?.assignments || []).slice(0, 3).map(a => ({
          assignment_id: a.assignment_id,
          name: a.name,
          short_description: a.short_description,
          title: a.title,
          date_due: a.date_due,
          due_date: a.due_date,
          dueDate: a.dueDate,
          due_at: a.due_at
        })));

        if (classData) {
          this.classData = classData;
        } else {
          this.errorMessage = classDataError || 'Failed to load class data.';
          return;
        }

        if (this.classData) {
          // Clean the class name
          if (this.classData.section_display_name) {
            this.classData.section_display_name = this.cleanField(this.classData.section_display_name);
          }

          if (this.classData.next_meeting) {
            this.nextMeetingTime = this.classData.next_meeting.start_time;
          } else {
            this.nextMeetingTime = null;
          }

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const normalizedAssignments = (Array.isArray(this.classData.assignments) ? this.classData.assignments : [])
            .map((assignment) => this.normalizeAssignment(assignment))
            .filter(Boolean);

          this.upcomingAssignments = normalizedAssignments
            .filter((assignment) => {
              const dueDate = this.parseDateTime(assignment.display_due_date);
              return dueDate && dueDate >= today;
            })
            .sort((a, b) => this.parseDateTime(a.display_due_date) - this.parseDateTime(b.display_due_date));

          const normalizedNextAssignment = this.classData.next_assignment_due
            ? this.normalizeAssignment(this.classData.next_assignment_due)
            : null;

          if (normalizedNextAssignment && normalizedNextAssignment.display_due_date) {
            this.nextAssignmentDue = normalizedNextAssignment;
          } else if (this.upcomingAssignments.length > 0) {
            this.nextAssignmentDue = this.upcomingAssignments[0];
          } else {
            this.nextAssignmentDue = null;
          }
          debugLog('[ClassPage] normalized assignments:', normalizedAssignments.length);
          debugLog('[ClassPage] upcoming assignments:', this.upcomingAssignments.length);
          debugLog('[ClassPage] nextAssignmentDue:', this.nextAssignmentDue);

          if (!normalizedAssignments.length) {
            debugWarn('[ClassPage] No assignments in classData; attempting /assignments fallback.');
            const fallback = await this.fetchAssignmentsForClass();
            if (fallback) {
              this.upcomingAssignments = fallback.upcomingAssignments;
              this.nextAssignmentDue = fallback.nextAssignmentDue;
            }
          }

          this.gradedAssignments = (Array.isArray(this.classData.graded_assignments) ? this.classData.graded_assignments : [])
            .filter(
              assignment => assignment.assignment_percentage !== null &&
                assignment.assignment_percentage !== undefined
            )
            .map(assignment => ({
              ...assignment,
              short_description: this.cleanField(assignment.short_description)
            }));
        }
      } catch (error) {
        console.error('Error fetching class page data:', error);
        if (error.response && error.response.status === 401) {
          this.redirectToLogin();
        } else {
          this.errorMessage = 'Failed to load class data.';
        }
      } finally {
        this.isLoading = false;
      }
    },

    updateBodyScroll() {
      if (this.showAssignmentModal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
  },
  watch: {
    showAssignmentModal() {
      this.updateBodyScroll();
    },
    async section_id(newId, oldId) {
      if (newId !== oldId) {
        this.isLoading = true;
        this.errorMessage = '';
        this.classData = null;
        this.nextMeetingTime = null;
        this.nextAssignmentDue = null;
        this.upcomingAssignments = [];
        this.gradedAssignments = [];
        await this.fetchClassData();
      }
    },
  },
  async created() {
    this.extractToken();
    if (this.token && !this.isTokenExpired(this.token)) {
      const totalStartTime = performance.now();
      console.log('[ClassPage] Starting to load class data...');

      await this.fetchClassData();

      const totalEndTime = performance.now();
      console.log(`[ClassPage] Total page load time: ${(totalEndTime - totalStartTime).toFixed(2)}ms (${((totalEndTime - totalStartTime) / 1000).toFixed(2)}s)`);
    } else {
      this.redirectToLogin();
    }
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  },
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #6366f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #4f46e5;
}

.dark::-webkit-scrollbar-thumb {
  background: #818cf8;
}

.dark::-webkit-scrollbar-thumb:hover {
  background: #6366f1;
}

table {
  width: 100%;
  border-spacing: 0;
}
</style>
